import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  getProductionGrain,
  updateProductionGrain,
  UpdateProductionGrainBody,
} from "@/services/production-grains.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { ProductionGrainListEntity } from "../../schema/production-grains.schema";
import { ProductionGrainCreationForm } from "../../create/schema/production-grain-creation.schema";
import { getBooleanValue } from "@/utils/request-validations";

interface FetchProductionGrainHandlerProps {
  id: number;
  setLoadingProductionGrain: Dispatch<SetStateAction<boolean>>;
  setProductionGrain: Dispatch<SetStateAction<ProductionGrainListEntity | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

interface UpdateProductionGrainHandlerProps {
  id: number;
  values: ProductionGrainCreationForm;
  setLoadingUpdate: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}


export const fetchProductionGrainHandler = async ({
  id,
  setLoadingProductionGrain,
  setProductionGrain,
  navigate,
  fetchPermissions,
}: FetchProductionGrainHandlerProps) => {
  try {
    setLoadingProductionGrain(true);

    const response = await getProductionGrain(id);
    setProductionGrain(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTION_GRAINS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          "No se pudo obtener el grano productivo.",
        ),
      });
    }

    throw error;
  } finally {
    setLoadingProductionGrain(false);
  }
};

export const updateProductionGrainHandler = async ({
  id,
  values,
  setLoadingUpdate,
  navigate,
  fetchPermissions,
}: UpdateProductionGrainHandlerProps) => {
  const body: UpdateProductionGrainBody = {
    id,
    nombre: String(values?.nombre ?? "").trim(),
    aplicaPPMB: getBooleanValue(values?.aplicaPPMB),
    granoAdmis: Number(values?.granoAdmis),
    sumaHA: getBooleanValue(values?.sumaHA),
  };

  try {
    setLoadingUpdate(true);

    await updateProductionGrain(body);

    toaster.create({
      type: "success",
      title: "Grano productivo actualizado exitosamente.",
    });

    navigate(AppRoutes.PRODUCTION_GRAINS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTION_GRAINS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          "No se pudo actualizar el grano productivo.",
        ),
      });
    }

    throw error;
  } finally {
    setLoadingUpdate(false);
  }
};
